import {
  getModulesByContext,
  getActiveModulesByContext,
  getModulePrimaryRoute,
  getModuleIcon,
  groupModulesByContext,
  ERP_MODULES_CONFIG,
  type NavigationContext,
  type Module,
} from "@/convex/lib/modules";
import { getIconComponent } from "./icon-mapper";
import type { modulesTypes } from "@/convex/system/permissions";

/**
 * Navigation item structure for UI components
 */
export interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  module: typeof modulesTypes.type;
  description?: string;
  badge?: string | number;
  disabled?: boolean;
  /**
   * Si es `true`, el item renderiza `item.name` literal sin pasar por i18n.
   * Pensado para items cuyo nombre es definido por el usuario (p. ej. nombres
   * de colecciones dinámicas) y no representa un módulo traducible del sistema.
   * Default: `false` (el sidebar traduce vía `modules.<id>` en navigation.json).
   */
  useLabel?: boolean;
  /** Icono del grupo con nombre al que pertenece (colecciones agrupadas). */
  groupIcon?: React.ComponentType<{ className?: string }>;
  /** Colección privada (el menú la marca con un candado). */
  private?: boolean;
}

/**
 * Navigation group structure
 */
export interface NavigationGroup {
  id: string;
  name: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  order?: number;
  items: NavigationItem[];
}

/**
 * Options for generating navigation
 */
export interface GenerateNavigationOptions {
  /** Filter by status (default: only active) */
  includeComingSoon?: boolean;
  /** Add custom badge to items */
  badgeProvider?: (moduleId: string) => string | number | undefined;
  /** Filter specific modules */
  moduleFilter?: (module: Module) => boolean;
  /** Custom route resolver */
  routeResolver?: (module: Module) => string;
}

/**
 * Generate navigation items for a specific context
 *
 * @example
 * // Generate main sidebar navigation
 * const navigation = generateNavigation("main");
 *
 * @example
 * // Generate with coming soon items
 * const navigation = generateNavigation("settings", {
 *   includeComingSoon: true
 * });
 */
export function generateNavigation(
  context: NavigationContext,
  options: GenerateNavigationOptions = {},
): NavigationItem[] {
  const {
    includeComingSoon = false,
    badgeProvider,
    moduleFilter,
    routeResolver,
  } = options;

  // Get modules for context
  let modules = includeComingSoon
    ? getModulesByContext(context)
    : getActiveModulesByContext(context);

  // Apply custom filter if provided
  if (moduleFilter) {
    modules = modules.filter(moduleFilter);
  }

  // Map to navigation items
  return modules.map((module) => {
    const iconName = getModuleIcon(module.id) || "HelpCircle";
    const route = routeResolver
      ? routeResolver(module)
      : getModulePrimaryRoute(module.id) || "#";

    const item: NavigationItem = {
      name: module.name,
      href: route,
      icon: getIconComponent(iconName),
      module: module.id as typeof modulesTypes.type,
      description: module.description,
      disabled: module.navigation?.status === "coming-soon",
    };

    // Add badge if provider exists
    if (badgeProvider) {
      const badge = badgeProvider(module.id);
      if (badge !== undefined) {
        item.badge = badge;
      }
    }

    return item;
  });
}

/**
 * Generate grouped navigation for a context
 * Useful for organizing navigation by categories
 *
 * @example
 * const groups = generateGroupedNavigation("super");
 * // Returns: [
 * //   { id: "system", name: "Sistema", items: [...] },
 * //   { id: "security", name: "Seguridad", items: [...] }
 * // ]
 */
export function generateGroupedNavigation(
  context: NavigationContext,
  options: GenerateNavigationOptions = {},
): NavigationGroup[] {
  const { includeComingSoon = false, routeResolver, moduleFilter } = options;
  const grouped = groupModulesByContext(context);
  const groups: NavigationGroup[] = [];

  Object.entries(grouped).forEach(([groupId, modules]) => {
    let filteredModules = modules;
    if (!includeComingSoon) {
      filteredModules = modules.filter(
        (m) => m.navigation?.status === "active",
      );
    }

    if (moduleFilter) {
      filteredModules = filteredModules.filter(moduleFilter);
    }

    if (filteredModules.length === 0) return;

    const groupConfig = ERP_MODULES_CONFIG.modules[groupId];
    const groupIconName = groupConfig?.icon || "Folder";
    const groupName = groupConfig?.name || groupId;
    const groupOrder = groupConfig?.order ?? 999;

    const items: NavigationItem[] = filteredModules.map((module) => {
      const iconName = getModuleIcon(module.id) || "HelpCircle";
      const route = routeResolver
        ? routeResolver(module)
        : getModulePrimaryRoute(module.id) || "#";

      return {
        name: module.name,
        href: route,
        icon: getIconComponent(iconName),
        module: module.id as typeof modulesTypes.type,
        description: module.description,
        disabled: module.navigation?.status === "coming-soon",
      };
    });

    groups.push({
      id: groupId,
      name: groupName,
      description: groupConfig?.description,
      icon: getIconComponent(groupIconName),
      order: groupOrder,
      items,
    });
  });

  groups.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  return groups;
}

/**
 * Generate navigation for main sidebar
 * Convenience function for most common use case
 */
export function generateMainNavigation(
  options?: GenerateNavigationOptions,
): NavigationItem[] {
  return generateNavigation("main", options);
}

/**
 * Generate navigation for settings menu
 * Convenience function for settings sidebar
 */
export function generateSettingsNavigation(
  options?: GenerateNavigationOptions,
): NavigationItem[] {
  return generateNavigation("settings", options);
}

/**
 * Generate navigation for super admin panel
 * Convenience function for super admin navigation
 */
export function generateSuperNavigation(
  options?: GenerateNavigationOptions,
): NavigationItem[] {
  return generateNavigation("super", options);
}

/**
 * Generate navigation for mobile bottom bar
 * Convenience function for mobile navigation
 */
export function generateMobileNavigation(
  options?: GenerateNavigationOptions,
): NavigationItem[] {
  return generateNavigation("mobile", options);
}

/**
 * Generate navigation for billing/invoices section
 * Convenience function for billing tabs
 */
export function generateBillingNavigation(
  options?: GenerateNavigationOptions,
): NavigationItem[] {
  return generateNavigation("billing", options);
}

/**
 * Find navigation item by module ID
 */
export function findNavigationItem(
  navigation: NavigationItem[],
  moduleId: string,
): NavigationItem | undefined {
  return navigation.find((item) => item.module === moduleId);
}

/**
 * Find navigation item by href
 */
export function findNavigationItemByHref(
  navigation: NavigationItem[],
  href: string,
): NavigationItem | undefined {
  return navigation.find((item) => item.href === href);
}

/**
 * Check if a path is active based on navigation item
 */
export function isNavigationItemActive(
  item: NavigationItem,
  currentPath: string,
): boolean {
  // Exact match
  if (item.href === currentPath) return true;

  // Check if current path starts with item href (for nested routes)
  if (currentPath.startsWith(item.href) && item.href !== "/") {
    return true;
  }

  return false;
}

/**
 * Get active navigation item from current path
 */
export function getActiveNavigationItem(
  navigation: NavigationItem[],
  currentPath: string,
): NavigationItem | undefined {
  // First try exact match
  let active = navigation.find((item) => item.href === currentPath);
  if (active) return active;

  // Then try prefix match (longest first)
  const sorted = [...navigation].sort((a, b) => b.href.length - a.href.length);
  active = sorted.find(
    (item) => item.href !== "/" && currentPath.startsWith(item.href),
  );

  return active;
}

/**
 * Navigation tab structure for PageNavigationTabs component
 * Uses icon names as strings (not components)
 */
export interface NavigationTab {
  name: string;
  href: string;
  icon?: string;
  description?: string;
  module?: typeof modulesTypes.type;
  badge?: string | number;
  disabled?: boolean;
}

/**
 * Generate navigation tabs for a specific context
 * Returns tabs with icon names as strings (for DynamicIcon component)
 *
 * @example
 * const tabs = generateNavigationTabs("billing");
 * // Returns: [
 * //   { name: "Facturas", href: "/invoices", icon: "FileText", module: "invoices" },
 * //   { name: "Pagos", href: "/invoices/payments", icon: "CreditCard", ... }
 * // ]
 */
export function generateNavigationTabs(
  context: NavigationContext,
  options: GenerateNavigationOptions = {},
): NavigationTab[] {
  const {
    includeComingSoon = false,
    badgeProvider,
    moduleFilter,
    routeResolver,
  } = options;

  // Get modules for context
  let modules = includeComingSoon
    ? getModulesByContext(context)
    : getActiveModulesByContext(context);

  // Apply custom filter if provided
  if (moduleFilter) {
    modules = modules.filter(moduleFilter);
  }

  // Map to navigation tabs
  return modules.map((module) => {
    const iconName = getModuleIcon(module.id);
    const route = routeResolver
      ? routeResolver(module)
      : getModulePrimaryRoute(module.id) || "#";

    const tab: NavigationTab = {
      name: module.name,
      href: route,
      icon: iconName,
      description: module.description,
      module: module.id as typeof modulesTypes.type,
      disabled: module.navigation?.status === "coming-soon",
    };

    // Add badge if provider exists
    if (badgeProvider) {
      const badge = badgeProvider(module.id);
      if (badge !== undefined) {
        tab.badge = badge;
      }
    }

    return tab;
  });
}

/**
 * Generate navigation tabs for billing/invoices section
 * Convenience function for billing tabs
 *
 * @example
 * const tabs = generateBillingTabs();
 */
export function generateBillingTabs(
  options?: GenerateNavigationOptions,
): NavigationTab[] {
  return generateNavigationTabs("billing", options);
}
